// Geometry kit for robot parts: compose primitives with per-face ink, wear and glow, bake an
// ink hull (inverted, pushed along smoothed normals) into the same buffer, and a Batch that
// owns one InstancedMesh per part shared by every robot of a type.

import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { PAL } from '../palette';
import { cellUv, type Cell } from './decals';

export type Num3 = number | ((x: number, y: number, z: number) => number);

export interface SubOpts {
  /** wear: rust amount 0..1 (drips print where the grunge drip map exceeds 1-rust) */
  rust?: Num3;
  /** chip amount 0..1 */
  chip?: number;
  /** glow colour (hex) lit by the part's glow amount; 0/undefined = none */
  glow?: number;
  /** bake an ink hull around this sub */
  outline?: boolean;
  /** per-face tone jitter (print unevenness) */
  jitter?: number;
  /** atlas cell: the geometry's own uv (0..1) maps into it; colour = atlas x vertex colour */
  decal?: Cell;
}

interface Sub { geo: THREE.BufferGeometry; color: Num3; o: SubOpts }

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();

/** Transform a geometry in place: scale, then rotate (XYZ euler), then translate. Returns it. */
export function T(g: THREE.BufferGeometry, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) {
  _q.setFromEuler(_e.set(rx, ry, rz));
  _m.compose(_p.set(x, y, z), _q, _s.set(sx, sy, sz));
  g.applyMatrix4(_m);
  return g;
}

const _c = new THREE.Color();
const ink = new THREE.Color(PAL.inkDark);

export class PartBuilder {
  private subs: Sub[] = [];
  private aoY0 = 0; private aoY1 = -1; private aoMin = 1;
  constructor(public outlineWidth = 0.03) {}
  /** baked ground occlusion: vertex colour falls to `min` at y0 and is full above y1 */
  ao(y0: number, y1: number, min = 0.72) { this.aoY0 = y0; this.aoY1 = y1; this.aoMin = min; return this; }

  add(geo: THREE.BufferGeometry, color: Num3, o: SubOpts = {}) {
    this.subs.push({ geo, color, o });
    return this;
  }

  build(): THREE.BufferGeometry {
    const pos: number[] = [], nor: number[] = [], col: number[] = [], mat: number[] = [], glo: number[] = [], uvs: number[] = [];
    let seed = 11;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();
    const glowC = new THREE.Color();
    for (const s of this.subs) {
      const g = s.geo.index ? s.geo.toNonIndexed() : s.geo;
      const P = g.attributes.position.array as ArrayLike<number>;
      const UV = s.o.decal && g.attributes.uv ? (g.attributes.uv.array as ArrayLike<number>) : null;
      const cr = s.o.decal ? cellUv(s.o.decal) : null;
      const N = g.attributes.normal ? (g.attributes.normal.array as ArrayLike<number>) : null;
      const tri = P.length / 9;
      const jit = s.o.jitter ?? 0.06;
      // drips are seeded from each sub's top edge: bake the distance below it
      let topY = -1e9;
      for (let i = 1; i < P.length; i += 3) if (P[i] > topY) topY = P[i];
      // rounded boxes wear through at their edges and corners (where the smooth normal turns)
      const edgy = s.geo instanceof RoundedBoxGeometry && !s.o.decal;
      const chipAmt = s.o.chip ?? 0.4;
      if (s.o.glow) glowC.set(s.o.glow); else glowC.setRGB(0, 0, 0);
      for (let t = 0; t < tri; t++) {
        a.fromArray(P, t * 9); b.fromArray(P, t * 9 + 3); c.fromArray(P, t * 9 + 6);
        e1.subVectors(b, a); e2.subVectors(c, a); n.crossVectors(e1, e2);
        if (n.lengthSq() < 1e-14) continue;
        n.normalize();
        const cx = (a.x + b.x + c.x) / 3, cy = (a.y + b.y + c.y) / 3, cz = (a.z + b.z + c.z) / 3;
        _c.set(typeof s.color === 'function' ? s.color(cx, cy, cz) : s.color);
        const kj = 1 + (rnd() - 0.5) * jit;
        for (let k = 0; k < 3; k++) {
          const v = k === 0 ? a : k === 1 ? b : c;
          if (UV && cr) uvs.push(cr[0] + (cr[2] - cr[0]) * UV[(t * 3 + k) * 2], cr[1] + (cr[3] - cr[1]) * UV[(t * 3 + k) * 2 + 1]);
          else uvs.push(0, 0);
          pos.push(v.x, v.y, v.z);
          // keep the primitive's own (smooth) normals so curved shells shade as curves
          const vi = (t * 3 + k) * 3;
          if (N && N[vi] * n.x + N[vi + 1] * n.y + N[vi + 2] * n.z > 0.2) nor.push(N[vi], N[vi + 1], N[vi + 2]);
          else nor.push(n.x, n.y, n.z);
          let ao = 1;
          if (this.aoY1 > this.aoY0) { const u = Math.max(0, Math.min(1, (v.y - this.aoY0) / (this.aoY1 - this.aoY0))); ao = this.aoMin + (1 - this.aoMin) * u * u * (3 - 2 * u); }
          col.push(_c.r * kj * ao, _c.g * kj * ao, _c.b * kj * ao);
          const rust = s.o.rust === undefined ? 0.25 : typeof s.o.rust === 'function' ? s.o.rust(v.x, v.y, v.z) : s.o.rust;
          // no scattered chips on flat faces or curved shells: enamel wears through at edges only
          let ch = 0;
          if (edgy && N) {
            const e = 1 - Math.max(Math.abs(N[vi]), Math.abs(N[vi + 1]), Math.abs(N[vi + 2]));
            const u = Math.max(0, Math.min(1, (e - 0.04) / 0.2));
            ch = chipAmt * 2.9 * u * u * (3 - 2 * u);
          }
          mat.push(0, s.o.decal ? 1 : 0, Math.max(0, Math.min(1, rust)), ch);
          // w: distance below this sub's top edge (drip origin); rides in the glow attribute
          // because the toon program is at the 16-attribute limit
          glo.push(glowC.r, glowC.g, glowC.b, topY - v.y);
        }
      }
      if (s.o.outline !== false && s.o.outline !== undefined) this.hull(P, pos, nor, col, mat, glo, uvs);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.setAttribute('aMat', new THREE.Float32BufferAttribute(mat, 4));
    geo.setAttribute('aGlow', new THREE.Float32BufferAttribute(glo, 4));
    geo.setAttribute('aUv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.computeBoundingSphere();
    return geo;
  }

  /** inverted hull: smoothed (position-welded) normals so faceted primitives don't crack */
  private hull(P: ArrayLike<number>, pos: number[], nor: number[], col: number[], mat: number[], glo: number[], uvs: number[]) {
    const key = (i: number) => `${Math.round(P[i] * 1e4)},${Math.round(P[i + 1] * 1e4)},${Math.round(P[i + 2] * 1e4)}`;
    const acc = new Map<string, THREE.Vector3>();
    const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3();
    const tri = P.length / 9;
    for (let t = 0; t < tri; t++) {
      a.fromArray(P, t * 9); b.fromArray(P, t * 9 + 3); c.fromArray(P, t * 9 + 6);
      n.crossVectors(b.clone().sub(a), c.clone().sub(a)); // area weighted
      for (let k = 0; k < 3; k++) {
        const id = key(t * 9 + k * 3);
        let v = acc.get(id);
        if (!v) { v = new THREE.Vector3(); acc.set(id, v); }
        v.add(n);
      }
    }
    for (const v of acc.values()) v.normalize();
    const w = this.outlineWidth;
    const order = [0, 2, 1];
    for (let t = 0; t < tri; t++) {
      for (const k of order) {
        const i = t * 9 + k * 3;
        const sn = acc.get(key(i))!;
        const h = Math.abs(Math.sin(P[i] * 12.9898 + P[i + 1] * 78.233 + P[i + 2] * 37.719) * 43758.5453) % 1;
        const ww = w * (0.75 + 0.5 * h);
        pos.push(P[i] + sn.x * ww, P[i + 1] + sn.y * ww, P[i + 2] + sn.z * ww);
        nor.push(-sn.x, -sn.y, -sn.z);
        col.push(ink.r, ink.g, ink.b);
        mat.push(1, 0, 0, 0);
        glo.push(0, 0, 0, 0);
        uvs.push(0, 0);
      }
    }
  }
}

/** Per-robot status values written into the robot's instance. */
export class Fx {
  /** emissive add rgb, whiten */
  fx = new Float32Array(4);
  /** rim rgb, char (death scorch 0..1) */
  rim = new Float32Array(4);
  seed = 0;
}


/** Cheap shadow caster for a part: its bounding box as a box (or an 8-sided can if round). */
export function proxyOf(g: THREE.BufferGeometry, round: boolean, shrink = 0.92): THREE.BufferGeometry {
  g.computeBoundingBox();
  const bb = g.boundingBox!;
  const sx = (bb.max.x - bb.min.x) * shrink, sy = bb.max.y - bb.min.y, sz = (bb.max.z - bb.min.z) * shrink;
  const p = round ? new THREE.CylinderGeometry(0.5, 0.5, 1, 8, 1) : new THREE.BoxGeometry(1, 1, 1);
  p.scale(Math.max(sx, 0.02), Math.max(sy, 0.02), Math.max(sz, 0.02));
  p.translate((bb.max.x + bb.min.x) / 2, (bb.max.y + bb.min.y) / 2, (bb.max.z + bb.min.z) / 2);
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', (p.toNonIndexed()).attributes.position);
  return out;
}

/** Merges a type's parts into ONE geometry; each vertex carries its part slot (aPart). */
export class Rig {
  private geos: THREE.BufferGeometry[] = [];
  private shapes: ('box' | 'round' | 'none' | THREE.BufferGeometry)[] = [];
  /** add a part geometry `count` times (one slot each); returns the first slot.
   *  `shadow` picks the part's shadow proxy shape. */
  add(g: THREE.BufferGeometry, count = 1, shadow: 'box' | 'round' | 'none' | THREE.BufferGeometry = 'box'): number {
    const first = this.geos.length;
    for (let i = 0; i < count; i++) { this.geos.push(g); this.shapes.push(shadow); }
    return first;
  }
  /** the shadow twin: one box / can per slot, same slots */
  buildProxy(): THREE.BufferGeometry {
    const pos: number[] = [], part: number[] = [];
    const cache = new Map<THREE.BufferGeometry, THREE.BufferGeometry>();
    this.geos.forEach((g, slot) => {
      const sh = this.shapes[slot];
      if (sh === 'none') return;
      let p = typeof sh === 'string' ? cache.get(g) : sh.index ? sh.toNonIndexed() : sh;
      if (!p) { p = proxyOf(g, sh === 'round'); cache.set(g, p); }
      const a = p.attributes.position.array as Float32Array;
      for (let i = 0; i < a.length; i++) pos.push(a[i]);
      for (let i = 0; i < a.length / 3; i++) part.push(slot);
    });
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    out.setAttribute('aPart', new THREE.Float32BufferAttribute(part, 1));
    out.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1.5, 0), 6);
    return out;
  }
  get slots() { return this.geos.length; }
  /** visible triangles' vertex count in the last build (the shadow proxy follows it) */
  mainCount = 0;
  build(withProxy = false): THREE.BufferGeometry {
    const main = this.buildMain();
    this.mainCount = main.attributes.position.count;
    if (!withProxy) return main;
    // append the shadow twin after the visible triangles: the colour pass draws [0, main),
    // the shadow pass draws [main, end) (TypeBatch swaps the draw range around the shadow draw)
    const px = this.buildProxy();
    const n = this.mainCount, m = px.attributes.position.count;
    const out = new THREE.BufferGeometry();
    for (const name of Object.keys(main.attributes)) {
      const at = main.attributes[name], isz = at.itemSize;
      const arr = new Float32Array((n + m) * isz);
      arr.set(at.array as Float32Array, 0);
      if (name === 'position' || name === 'aPart') arr.set(px.attributes[name].array as Float32Array, n * isz);
      out.setAttribute(name, new THREE.Float32BufferAttribute(arr, isz));
    }
    out.boundingSphere = main.boundingSphere;
    out.setDrawRange(0, n);
    return out;
  }
  private buildMain(): THREE.BufferGeometry {
    const names = Object.keys(this.geos[0].attributes);
    let n = 0;
    for (const g of this.geos) n += g.attributes.position.count;
    const out = new THREE.BufferGeometry();
    const part = new Float32Array(n);
    for (const name of names) {
      const isz = this.geos[0].attributes[name].itemSize;
      const arr = new Float32Array(n * isz);
      let o = 0, v = 0;
      this.geos.forEach((g, slot) => {
        const a = g.attributes[name].array as Float32Array;
        arr.set(a, o); o += a.length;
        if (name === 'position') { part.fill(slot, v, v + g.attributes.position.count); v += g.attributes.position.count; }
      });
      out.setAttribute(name, new THREE.Float32BufferAttribute(arr, isz));
    }
    out.setAttribute('aPart', new THREE.Float32BufferAttribute(part, 1));
    out.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1.5, 0), 6);
    return out;
  }
}

/**
 * One robot TYPE = one InstancedMesh (one draw + one shadow draw for every robot of that
 * type). The instance matrix is the robot root; each rigid part's local matrix (and its glow
 * amount) lives in a float texture row per instance, fetched in the vertex shader by
 * (aPart, gl_InstanceID). Layout: 4 texels per slot: 3 matrix rows + (glow, 0, 0, 0).
 */
export class TypeBatch {
  readonly mesh: THREE.InstancedMesh;
  readonly tex: THREE.DataTexture;
  readonly cap: number;
  readonly slots: number;
  n = 0;
  private data: Float32Array;
  private aFx: THREE.InstancedBufferAttribute;
  private aRim: THREE.InstancedBufferAttribute;
  private aSeed: THREE.InstancedBufferAttribute;
  private fx: Float32Array;
  private rim: Float32Array;
  private seed: Float32Array;
  private row = 0;
  /** the shield skin (fx/shieldskin.ts): the same geometry and part texture drawn once more, pushed off the parts.
   *  Per instance: iSh (shield, hit age, break age, seed), iShHit (the world strike point). Null until addShield. */
  shield: THREE.InstancedMesh | null = null;
  private sh: Float32Array | null = null;
  private shHit: Float32Array | null = null;
  private aSh: THREE.InstancedBufferAttribute | null = null;
  private aShHit: THREE.InstancedBufferAttribute | null = null;
  private shOn = false;

  constructor(geo: THREE.BufferGeometry, slots: number, cap: number, castShadow: boolean, name: string,
    makeMat: (tex: THREE.DataTexture) => { color: THREE.Material; depth: THREE.Material },
    shadowFrom = 0) {
    this.cap = cap;
    this.slots = slots;
    this.data = new Float32Array(slots * 4 * cap * 4);
    this.tex = new THREE.DataTexture(this.data, slots * 4, cap, THREE.RGBAFormat, THREE.FloatType);
    this.tex.minFilter = this.tex.magFilter = THREE.NearestFilter;
    this.tex.generateMipmaps = false;
    this.tex.needsUpdate = true;
    this.fx = new Float32Array(cap * 4);
    this.rim = new Float32Array(cap * 4);
    this.seed = new Float32Array(cap);
    this.aFx = new THREE.InstancedBufferAttribute(this.fx, 4).setUsage(THREE.DynamicDrawUsage);
    this.aRim = new THREE.InstancedBufferAttribute(this.rim, 4).setUsage(THREE.DynamicDrawUsage);
    this.aSeed = new THREE.InstancedBufferAttribute(this.seed, 1).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iFx', this.aFx);
    geo.setAttribute('iRim', this.aRim);
    geo.setAttribute('iSeed', this.aSeed);
    const m = makeMat(this.tex);
    this.mesh = new THREE.InstancedMesh(geo, m.color, cap);
    this.mesh.customDepthMaterial = m.depth;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = castShadow;
    this.mesh.receiveShadow = true;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.name = name;
    if (shadowFrom > 0) {
      // shadows from the box-and-can twin at the end of the buffer, not the full mesh
      const g = geo, full = g.drawRange.count;
      this.mesh.onBeforeShadow = () => { g.setDrawRange(shadowFrom, Infinity); };
      this.mesh.onAfterShadow = () => { g.setDrawRange(0, full); };
    }
  }

  /** give this type a shield skin draw (material from fx/shieldskin.ts robotShieldMaterial, built on this.tex) */
  addShield(mat: THREE.Material) {
    const cap = this.cap, geo = this.mesh.geometry;
    this.sh = new Float32Array(cap * 4).fill(-1); this.shHit = new Float32Array(cap * 3);
    this.aSh = new THREE.InstancedBufferAttribute(this.sh, 4).setUsage(THREE.DynamicDrawUsage);
    this.aShHit = new THREE.InstancedBufferAttribute(this.shHit, 3).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iSh', this.aSh); geo.setAttribute('iShHit', this.aShHit);
    const m = new THREE.InstancedMesh(geo, mat, cap);
    m.instanceMatrix = this.mesh.instanceMatrix; // the same robots, the same roots
    m.frustumCulled = false; m.castShadow = false; m.receiveShadow = false;
    m.renderOrder = 2; m.count = 0; m.visible = false; m.name = this.mesh.name + '-shield';
    this.shield = m;
    return m;
  }

  /** the shield skin of the robot started last: k 0..1, seconds since a hit on it / since it broke (-1 none) */
  shieldOf(k: number, hitAge: number, brkAge: number, seed: number, hx: number, hy: number, hz: number) {
    if (!this.sh) return;
    const i = this.n - 1, o = i * 4, h = this.shHit!;
    this.sh[o] = k; this.sh[o + 1] = hitAge; this.sh[o + 2] = brkAge; this.sh[o + 3] = seed;
    h[i * 3] = hx; h[i * 3 + 1] = hy; h[i * 3 + 2] = hz;
    if (k > 0.001 || (hitAge >= 0 && hitAge < 0.7) || (brkAge >= 0 && brkAge < 0.5)) this.shOn = true;
  }

  begin() { this.n = 0; this.shOn = false; }

  /** start a robot: root matrix + status. Clears its part row (unwritten parts are hidden). */
  robot(root: THREE.Matrix4, f: Fx): boolean {
    const i = this.n;
    if (i >= this.cap) return false;
    root.toArray(this.mesh.instanceMatrix.array as Float32Array, i * 16);
    const o = i * 4;
    this.fx[o] = f.fx[0]; this.fx[o + 1] = f.fx[1]; this.fx[o + 2] = f.fx[2]; this.fx[o + 3] = f.fx[3];
    this.rim[o] = f.rim[0]; this.rim[o + 1] = f.rim[1]; this.rim[o + 2] = f.rim[2]; this.rim[o + 3] = f.rim[3];
    this.seed[i] = f.seed;
    if (this.sh) { this.sh[o] = 0; this.sh[o + 1] = -1; this.sh[o + 2] = -1; }
    this.row = i * this.slots * 16;
    this.data.fill(0, this.row, this.row + this.slots * 16);
    this.n = i + 1;
    return true;
  }

  /** write one part (local to the robot root) of the robot started last */
  part(slot: number, m: THREE.Matrix4, glow: number) {
    if (slot >= this.slots) return;
    const d = this.data, o = this.row + slot * 16, e = m.elements;
    d[o] = e[0]; d[o + 1] = e[4]; d[o + 2] = e[8]; d[o + 3] = e[12];
    d[o + 4] = e[1]; d[o + 5] = e[5]; d[o + 6] = e[9]; d[o + 7] = e[13];
    d[o + 8] = e[2]; d[o + 9] = e[6]; d[o + 10] = e[10]; d[o + 11] = e[14];
    d[o + 12] = glow;
  }

  end() {
    const n = this.n;
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (this.shield) {
      this.shield.count = n; this.shield.visible = n > 0 && this.shOn;
      if (this.shield.visible) {
        this.aSh!.clearUpdateRanges(); this.aSh!.addUpdateRange(0, n * 4); this.aSh!.needsUpdate = true;
        this.aShHit!.clearUpdateRanges(); this.aShHit!.addUpdateRange(0, n * 3); this.aShHit!.needsUpdate = true;
      }
    }
    if (!n) return;
    const im = this.mesh.instanceMatrix;
    im.clearUpdateRanges(); im.addUpdateRange(0, n * 16); im.needsUpdate = true;
    this.aFx.clearUpdateRanges(); this.aFx.addUpdateRange(0, n * 4); this.aFx.needsUpdate = true;
    this.aRim.clearUpdateRanges(); this.aRim.addUpdateRange(0, n * 4); this.aRim.needsUpdate = true;
    this.aSeed.clearUpdateRanges(); this.aSeed.addUpdateRange(0, n); this.aSeed.needsUpdate = true;
    this.tex.needsUpdate = true;
  }

  dispose() {
    this.mesh.geometry.dispose(); this.tex.dispose();
    (this.mesh.material as THREE.Material).dispose(); this.mesh.customDepthMaterial?.dispose();
    if (this.shield) { (this.shield.material as THREE.Material).dispose(); this.shield.dispose(); }
    this.mesh.dispose();
  }
}
